/* eslint-disable prettier/prettier */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import styles from '../../styles/components/pageComponents/ViewLearnerUploads.module.css';
import {
    Save,
    Pencil,
    Eye
} from 'lucide-react';
import {
    EMPTY_DISPLAY_VALUE,
    formatDisplayDate,
    getFileNameWithoutExtension,
} from '../../utils/fileDisplay';

export interface LearnerUpload {
    id: string;
    fileId: string;
    submissionId?: string;

    uploadedBy: string;
    assignmentName: string;
    fileType: string;
    fileUrl?: string;

    // Original uploaded file name (extension is hidden in the UI)
    fileName?: string | null;
    // When the file was uploaded
    uploadedAt?: string | Date | null;

    // null = not reviewed
    // true = passed
    // false = failed
    isApproved?: boolean | null;
}

export interface ViewLearnerUploadsProps {
    /** Component modal/header title */
    title?: string;

    /** List of uploads data */
    uploads?: LearnerUpload[];

    /** Callback triggered when closing the view/modal */
    onClose?: () => void;

    /** Callback triggered when a file download is initiated */
    onDownload?: (upload: LearnerUpload) => void;

    /**
     * Callback triggered when a row approval is saved.
     * Must return successfully before the component exits edit mode.
     */
    onSaveApproval?: (
        upload: LearnerUpload,
        isApproved: boolean
    ) => Promise<void> | void;

    /** Pass true to showcase loading skeleton state */
    isLoading?: boolean;

    /** Disable review save while API request is running */
    isReviewing?: boolean;

    /** Callback triggered when a supported file is opened for preview */
    onView?: (upload: LearnerUpload) => void;
}

export type ApprovalStatusFilter = 'all' | 'approved' | 'rejected' | 'pending';

export const ViewLearnerUploads: React.FC<ViewLearnerUploadsProps> = ({
    title = 'View Learner Uploads',
    uploads = [],
    onClose,
    onDownload,
    onView,
    onSaveApproval,
    isLoading = false,
    isReviewing = false,
}) => {

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown menu when clicking anywhere outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);



    // Tracks editing mode per row ID
    const [editingMap, setEditingMap] = useState<Record<string, boolean>>({});

    // Tracks checkbox state per row ID
    const [approvalMap, setApprovalMap] = useState<Record<string, boolean>>({});

    const [statusFilter, setStatusFilter] = useState<ApprovalStatusFilter>('all');

    const getItemStatus = (item: LearnerUpload): 'approved' | 'rejected' | 'pending' => {
        if (approvalMap[item.id] !== undefined) {
            return approvalMap[item.id] ? 'approved' : 'rejected';
        }
        if (item.isApproved === true) return 'approved';
        if (item.isApproved === false) return 'rejected';
        return 'pending';
    };


    const statusOptions: { label: string; value: ApprovalStatusFilter }[] = [
        { label: 'All', value: 'all' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
    ];

    // Gets the label string for the currently selected status
    const currentStatusLabel =
        statusOptions.find(opt => opt.value === statusFilter)?.label || 'All';
    const [searchQuery, setSearchQuery] = useState('');


    /** Combined filter for Search Query and Approval Status */
    const filteredUploads = useMemo(() => {
        return uploads.filter(item => {
            // Text Search
            const query = searchQuery.toLowerCase().trim();
            const matchesSearch =
                !query ||
                item.uploadedBy.toLowerCase().includes(query) ||
                item.assignmentName.toLowerCase().includes(query) ||
                item.fileType.toLowerCase().includes(query) ||
                (item.fileName ?? '').toLowerCase().includes(query);

            if (!matchesSearch) return false;

            // Status Filter
            if (statusFilter === 'all') return true;
            return getItemStatus(item) === statusFilter;
        });
    }, [uploads, searchQuery, statusFilter, approvalMap]);

    const handleResetFilters = () => {
        setSearchQuery('');
        setStatusFilter('all');
    };

    const handleDownload = (item: LearnerUpload) => {
        onDownload?.(item);
    };

    const isViewableFile = (item: LearnerUpload): boolean => {
        const fileType = item.fileType
            ?.toLowerCase()
            .trim()
            .replace('.', '');

        return ['pdf', 'doc', 'docx'].includes(fileType);
    };

    const handleView = (item: LearnerUpload) => {
        if (!isViewableFile(item)) {
            return;
        }

        onView?.(item);
    };
    /**
     * null/undefined from backend means "not reviewed yet".
     * For checkbox UI that naturally appears unchecked.
     */
    const getIsApproved = (item: LearnerUpload): boolean => {
        return approvalMap[item.id] ?? item.isApproved ?? false;
    };

    const handleCheckboxChange = (id: string, checked: boolean) => {
        setApprovalMap(prev => ({
            ...prev,
            [id]: checked,
        }));
    };

    const handleToggleEditSave = async (item: LearnerUpload) => {
        const isEditing = !!editingMap[item.id];

        /*
         * EDIT MODE
         */
        if (!isEditing) {
            setEditingMap(prev => ({
                ...prev,
                [item.id]: true,
            }));

            /*
             * Initialize checkbox using the value received
             * from backend.
             *
             * null/undefined => false in UI.
             */
            if (approvalMap[item.id] === undefined) {
                setApprovalMap(prev => ({
                    ...prev,
                    [item.id]: item.isApproved ?? false,
                }));
            }

            return;
        }

        /*
         * SAVE MODE
         */
        const currentApproval = getIsApproved(item);

        try {
            /*
             * Parent performs the review API call.
             * If this throws, we remain in edit mode.
             */
            await onSaveApproval?.(item, currentApproval);

            /*
             * Only leave edit mode after successful API operation.
             */
            setEditingMap(prev => ({
                ...prev,
                [item.id]: false,
            }));
        } catch (error) {
            console.error(
                '❌ Failed to save learner submission approval:',
                error
            );

            /*
             * Keep row in edit mode so user can retry.
             */
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div
                className={styles.container}
                role="dialog"
                aria-labelledby="modal-title"
            >
                {/* Header Section */}
                <header className={styles.header}>
                    <h2 id="modal-title" className={styles.title}>
                        {title}
                    </h2>

                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close modal"
                        disabled={isReviewing}
                    >
                        <svg
                            className={styles.closeIcon}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </header>

                {/* Search Input Section */}
                {/* Replace search bar area with this combined controls bar */}
                <div className={styles.controlsContainer}>
                    {/* Existing Search Input */}
                    <div className={styles.searchInputWrapper}>
                        <svg className={styles.searchIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search Learner"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            disabled={isReviewing}
                        />
                        {searchQuery && (
                            <button type="button" className={styles.clearSearchButton} onClick={() => setSearchQuery('')}>
                                ✕
                            </button>
                        )}
                    </div>

                    {/* NEW: Approval Status Select Dropdown */}
                    {/* Custom Dropdown Filter */}
                    <div className={styles.filterSelectWrapper} ref={dropdownRef}>
                        <button
                            type="button"
                            className={styles.dropdownTrigger}
                            onClick={() => setIsDropdownOpen(prev => !prev)}
                            disabled={isReviewing}
                            aria-haspopup="listbox"
                            aria-expanded={isDropdownOpen}
                        >
                            <span>{currentStatusLabel}</span>
                            <svg
                                className={`${styles.dropdownChevron} ${isDropdownOpen ? styles.chevronRotated : ''
                                    }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </button>

                        {/* Floating Popover Menu */}
                        {isDropdownOpen && (
                            <div className={styles.dropdownMenu} role="listbox">
                                {statusOptions.map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`${styles.dropdownItem} ${statusFilter === option.value ? styles.selectedItem : ''
                                            }`}
                                        onClick={() => {
                                            setStatusFilter(option.value);
                                            setIsDropdownOpen(false);
                                        }}
                                        role="option"
                                        aria-selected={statusFilter === option.value}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Content Area */}
                <div className={styles.contentArea}>
                    {isLoading ? (
                        <div className={styles.skeletonContainer}>
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    className={styles.skeletonRow}
                                />
                            ))}
                        </div>
                    ) : filteredUploads.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p className={styles.emptyText}>
                                {searchQuery || statusFilter !== 'all'
                                    ? 'No uploads found matching the selected search or filter criteria.'
                                    : 'No learner uploads available.'}
                            </p>

                            {(searchQuery || statusFilter !== 'all') && (
                                <button type="button" className={styles.resetButton} onClick={handleResetFilters}>
                                    Clear filters
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Desktop & Tablet Table View */}
                            <div className={styles.tableWrapper}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th scope="col">Uploaded by</th>

                                            <th scope="col">
                                                Assignment Name
                                            </th>

                                            <th scope="col">File Name</th>

                                            <th
                                                scope="col"
                                                className={styles.noWrap}
                                            >
                                                Uploaded Date
                                            </th>

                                            <th scope="col">File Type</th>

                                            <th
                                                scope="col"
                                                className={styles.alignCenter}
                                            >
                                                Approved
                                            </th>

                                            <th
                                                scope="col"
                                                className={styles.alignCenter}
                                            >
                                                Quick Actions
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredUploads.map(
                                            (item, index) => {
                                                const isEditing =
                                                    !!editingMap[item.id];

                                                const isApproved =
                                                    getIsApproved(item);

                                                const displayFileName =
                                                    getFileNameWithoutExtension(
                                                        item.fileName
                                                    );

                                                return (
                                                    <tr
                                                        key={item.id}
                                                        className={
                                                            index % 2 === 0
                                                                ? styles.stripedRow
                                                                : styles.normalRow
                                                        }
                                                    >
                                                        <td
                                                            title={
                                                                item.uploadedBy
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    styles.cellText
                                                                }
                                                            >
                                                                {
                                                                    item.uploadedBy
                                                                }
                                                            </span>
                                                        </td>

                                                        <td
                                                            title={
                                                                item.assignmentName
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    styles.cellText
                                                                }
                                                            >
                                                                {
                                                                    item.assignmentName
                                                                }
                                                            </span>
                                                        </td>

                                                        <td
                                                            title={
                                                                displayFileName !==
                                                                EMPTY_DISPLAY_VALUE
                                                                    ? displayFileName
                                                                    : undefined
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    styles.cellText
                                                                }
                                                            >
                                                                {
                                                                    displayFileName
                                                                }
                                                            </span>
                                                        </td>

                                                        <td
                                                            className={
                                                                styles.noWrap
                                                            }
                                                        >
                                                            {formatDisplayDate(
                                                                item.uploadedAt
                                                            )}
                                                        </td>

                                                        <td>
                                                            <span
                                                                className={
                                                                    styles.fileTypeBadge
                                                                }
                                                            >
                                                                {item.fileType}
                                                            </span>
                                                        </td>

                                                        <td
                                                            className={
                                                                styles.alignCenter
                                                            }
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className={
                                                                    styles.checkbox
                                                                }
                                                                checked={
                                                                    isApproved
                                                                }
                                                                disabled={
                                                                    !isEditing ||
                                                                    isReviewing
                                                                }
                                                                onChange={e =>
                                                                    handleCheckboxChange(
                                                                        item.id,
                                                                        e.target
                                                                            .checked
                                                                    )
                                                                }
                                                                aria-label={`Approved status for ${item.uploadedBy}`}
                                                            />
                                                        </td>

                                                        <td
                                                            className={
                                                                styles.alignCenter
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    styles.actionButtonsGroup
                                                                }
                                                            >

                                                                {/* View */}
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.actionButton} ${!isViewableFile(item) ? styles.disabledAction : ''
                                                                        }`}
                                                                    onClick={() => handleView(item)}
                                                                    aria-label={`View file for ${item.uploadedBy}`}
                                                                    title={
                                                                        isViewableFile(item)
                                                                            ? 'View file'
                                                                            : 'Preview not available for this file type'
                                                                    }
                                                                    disabled={!isViewableFile(item) || isReviewing}
                                                                >
                                                                    <Eye className={styles.actionIcon} />
                                                                </button>
                                                                {/* Download */}
                                                                <button
                                                                    type="button"
                                                                    className={
                                                                        styles.actionButton
                                                                    }
                                                                    onClick={() =>
                                                                        handleDownload(
                                                                            item
                                                                        )
                                                                    }
                                                                    aria-label={`Download file for ${item.uploadedBy}`}
                                                                    title="Download file"
                                                                    disabled={
                                                                        isReviewing
                                                                    }
                                                                >
                                                                    <svg
                                                                        className={
                                                                            styles.actionIcon
                                                                        }
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                        aria-hidden="true"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={
                                                                                1.8
                                                                            }
                                                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                                                        />
                                                                    </svg>
                                                                </button>

                                                                {/* Edit / Save */}
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.actionButton} ${isEditing
                                                                        ? styles.saveMode
                                                                        : ''
                                                                        }`}
                                                                    onClick={() =>
                                                                        handleToggleEditSave(
                                                                            item
                                                                        )
                                                                    }
                                                                    aria-label={
                                                                        isEditing
                                                                            ? `Save approval for ${item.uploadedBy}`
                                                                            : `Edit approval for ${item.uploadedBy}`
                                                                    }
                                                                    title={
                                                                        isReviewing
                                                                            ? 'Saving...'
                                                                            : isEditing
                                                                                ? 'Save'
                                                                                : 'Edit'
                                                                    }
                                                                    disabled={
                                                                        isReviewing
                                                                    }
                                                                >
                                                                    {isEditing ? (
                                                                        <Save className={styles.actionIcon} />
                                                                    ) : (
                                                                        <Pencil className={styles.actionIcon} />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card List View */}
                            <div className={styles.mobileCardList}>
                                {filteredUploads.map(item => {
                                    const isEditing =
                                        !!editingMap[item.id];

                                    const isApproved =
                                        getIsApproved(item);

                                    return (
                                        <div
                                            key={item.id}
                                            className={styles.mobileCard}
                                        >
                                            <div
                                                className={
                                                    styles.mobileCardHeader
                                                }
                                            >
                                                <div>
                                                    <h4
                                                        className={
                                                            styles.mobileLearnerName
                                                        }
                                                    >
                                                        {item.uploadedBy}
                                                    </h4>

                                                    <p
                                                        className={
                                                            styles.mobileAssignmentName
                                                        }
                                                    >
                                                        {
                                                            item.assignmentName
                                                        }
                                                    </p>
                                                </div>

                                                <div
                                                    className={
                                                        styles.actionButtonsGroup
                                                    }
                                                >
                                                    <button
                                                        type="button"
                                                        className={`${styles.actionButton} ${!isViewableFile(item) ? styles.disabledAction : ''
                                                            }`}
                                                        onClick={() => handleView(item)}
                                                        title={
                                                            isViewableFile(item)
                                                                ? 'View file'
                                                                : 'Preview not available for this file type'
                                                        }
                                                        disabled={!isViewableFile(item) || isReviewing}
                                                    >
                                                        <Eye className={styles.actionIcon} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={
                                                            styles.actionButton
                                                        }
                                                        onClick={() =>
                                                            handleDownload(
                                                                item
                                                            )
                                                        }
                                                        title="Download file"
                                                        disabled={
                                                            isReviewing
                                                        }
                                                    >
                                                        <svg
                                                            className={
                                                                styles.actionIcon
                                                            }
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={
                                                                    1.8
                                                                }
                                                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className={`${styles.actionButton} ${isEditing
                                                            ? styles.saveMode
                                                            : ''
                                                            }`}
                                                        onClick={() =>
                                                            handleToggleEditSave(
                                                                item
                                                            )
                                                        }
                                                        title={
                                                            isReviewing
                                                                ? 'Saving...'
                                                                : isEditing
                                                                    ? 'Save'
                                                                    : 'Edit'
                                                        }
                                                        disabled={
                                                            isReviewing
                                                        }
                                                    >
                                                        {isEditing ? (
                                                            <Save className={styles.actionIcon} />
                                                        ) : (
                                                            <Pencil className={styles.actionIcon} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            <div
                                                className={
                                                    styles.mobileCardFooter
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.mobileMetaRow
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.mobileMetaLabel
                                                        }
                                                    >
                                                        File Name:
                                                    </span>

                                                    <span
                                                        className={
                                                            styles.cellText
                                                        }
                                                        title={getFileNameWithoutExtension(
                                                            item.fileName
                                                        )}
                                                    >
                                                        {getFileNameWithoutExtension(
                                                            item.fileName
                                                        )}
                                                    </span>
                                                </div>

                                                <div
                                                    className={
                                                        styles.mobileMetaRow
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.mobileMetaLabel
                                                        }
                                                    >
                                                        Uploaded:
                                                    </span>

                                                    <span
                                                        className={
                                                            styles.fileTypeBadge
                                                        }
                                                    >
                                                        {formatDisplayDate(
                                                            item.uploadedAt
                                                        )}
                                                    </span>
                                                </div>

                                                <div
                                                    className={
                                                        styles.mobileMetaRow
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.mobileMetaLabel
                                                        }
                                                    >
                                                        File Type:
                                                    </span>

                                                    <span
                                                        className={
                                                            styles.fileTypeBadge
                                                        }
                                                    >
                                                        {item.fileType}
                                                    </span>
                                                </div>

                                                <div
                                                    className={
                                                        styles.mobileMetaRow
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.mobileMetaLabel
                                                        }
                                                    >
                                                        Approved:
                                                    </span>

                                                    <input
                                                        type="checkbox"
                                                        className={
                                                            styles.checkbox
                                                        }
                                                        checked={
                                                            isApproved
                                                        }
                                                        disabled={
                                                            !isEditing ||
                                                            isReviewing
                                                        }
                                                        onChange={e =>
                                                            handleCheckboxChange(
                                                                item.id,
                                                                e.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewLearnerUploads;